/*
 * Review Drawer — scoped to [data-main-product].
 * Hooks: [data-review-trigger], [data-review-overlay], [data-review-drawer],
 *        [data-review-close], [data-star-rating], [data-star], [data-review-rating-value],
 *        [data-review-form], [data-review-submit], [data-review-error].
 * State: data-review-open="false|true" on [data-main-product] (setAttribute only).
 */

(function () {
  /* global createFocusTrap */

  // NB-runtime-2: the trigger-open (click) and Escape-to-close (keydown) listeners are
  // document-level (D25 cross-section delegation). They are bound ONCE at module scope and
  // dispatched to the set of live instances — NOT re-bound per constructor. In the Shopify
  // editor `shopify:section:load` re-inits this script on every section swap; binding inside
  // the constructor would stack a fresh pair of document listeners (referencing a detached
  // root) on each reload. Instances self-register on construction and are pruned when their
  // root leaves the DOM (`shopify:section:unload`, plus a lazy isConnected sweep on dispatch).
  const instances = new Set();

  // Past the panel's slide (`transition: translate 0.3s` on [data-review-drawer]), with margin.
  const RESET_DELAY_MS = 600;

  class ReviewDrawer {
    constructor(root) {
      this.root        = root;
      this.overlay     = root.querySelector('[data-review-overlay]');
      this.panel       = root.querySelector('[data-review-drawer]');
      this.closeBtn    = root.querySelector('[data-review-close]');
      this.triggers    = [...root.querySelectorAll('[data-review-trigger]')];
      this.starGroup   = root.querySelector('[data-star-rating]');
      this.stars       = this.starGroup ? [...this.starGroup.querySelectorAll('[data-star]')] : [];
      this.ratingInput = root.querySelector('[data-review-rating-value]');
      this.form        = root.querySelector('[data-review-form]');
      this.submitBtn   = root.querySelector('[data-review-submit]');
      this.errorEl     = root.querySelector('[data-review-error]');
      this.successEl   = root.querySelector('[data-review-success]');

      this.rating      = 0;
      this.lastTrigger = null;
      this.resetTimer  = null;
      this.focusTrap   = typeof createFocusTrap === 'function' ? createFocusTrap(this.panel) : null;

      if (!this.panel) return;
      this.root.setAttribute('data-review-bound', '');
      this.bindEvents();
      this.updateRovingTabindex();
      instances.add(this);
    }

    bindEvents() {
      // Element-scoped listeners only — these live and die with this.root, so re-init on a
      // section reload cannot accumulate them. The two document-level listeners (trigger-open
      // delegation per D25 + Escape-to-close) are bound once at module scope; see the
      // handleDocument* methods and the module-scoped wiring below (NB-runtime-2).
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.closeDrawer());
      if (this.overlay)  this.overlay.addEventListener('click', () => this.closeDrawer());
      // Continue shopping (shown in the confirmation state) just closes the drawer.
      const continueBtn = this.root.querySelector('[data-review-continue]');
      if (continueBtn) continueBtn.addEventListener('click', () => this.closeDrawer());

      this.bindStars();

      if (this.form) {
        this.form.addEventListener('input', () => this.checkValidity());
        this.form.addEventListener('change', () => this.checkValidity());
        this.form.addEventListener('submit', (e) => this.handleSubmit(e));
      }
    }

    // D25: document-level delegation — the WRITE pill ([data-review-trigger]) lives in the
    // sibling product-reviews section, outside this drawer's [data-main-product] root, so a
    // constructor-time querySelectorAll cannot find it. Dispatched from the single module-scoped
    // click listener; disambiguate by aria-controls === this.panel.id (or single-drawer fallback).
    handleDocumentClick(e) {
      const trigger = e.target.closest('[data-review-trigger]');
      if (!trigger || !this.ownsTrigger(trigger)) return;
      this.lastTrigger = trigger;
      this.openDrawer();
    }

    handleDocumentKeydown(e) {
      if (e.key === 'Escape' && this.root.dataset.reviewOpen === 'true') this.closeDrawer();
    }

    destroy() {
      instances.delete(this);
      clearTimeout(this.resetTimer);
      if (this.focusTrap) this.focusTrap.deactivate();
    }

    ownsTrigger(trigger) {
      return (
        trigger.getAttribute('aria-controls') === this.panel.id ||
        (!trigger.getAttribute('aria-controls') &&
          document.querySelectorAll('[data-main-product]').length === 1)
      );
    }

    openDrawer() {
      // Run a close's pending reset now, so a fast reopen still lands on an empty form.
      if (this.resetTimer) { clearTimeout(this.resetTimer); this.resetTimer = null; this.resetForm(); }
      // Reopening after a submit returns to the form (clears the thank-you confirmation).
      this.root.removeAttribute('data-review-submitted');
      this.root.setAttribute('data-review-open', 'true');
      this.triggers.forEach(t => t.setAttribute('aria-expanded', 'true'));
      // the cross-section pill is not in this.triggers — set its state directly (L-RS1)
      if (this.lastTrigger) this.lastTrigger.setAttribute('aria-expanded', 'true');
      this.panel.removeAttribute('aria-hidden');
      this.panel.removeAttribute('inert');
      document.documentElement.setAttribute('data-scroll-lock', '');
      if (this.focusTrap) this.focusTrap.activate();
      if (this.closeBtn) this.closeBtn.focus();
    }

    closeDrawer() {
      this.root.setAttribute('data-review-open', 'false');
      this.triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
      if (this.lastTrigger) this.lastTrigger.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('inert', '');
      document.documentElement.removeAttribute('data-scroll-lock');
      if (this.focusTrap) this.focusTrap.deactivate();
      if (this.lastTrigger) this.lastTrigger.focus();
      // The drawer opens empty: a half-written review is not left for whoever opens it next. Deferred
      // so the fields are not seen emptying, and skipped if the drawer reopened in the meantime.
      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => {
        this.resetTimer = null;
        if (this.root.dataset.reviewOpen !== 'true') this.resetForm();
      }, RESET_DELAY_MS);
    }

    // Successful post: keep the drawer open and swap the form for the thank-you confirmation
    // (form + footer hide via [data-review-submitted]; CSS). Move focus to the message so it's
    // announced (role="status") and keyboard focus isn't left on the now-hidden submit button.
    showSuccess() {
      this.root.setAttribute('data-review-submitted', 'true');
      if (this.successEl) this.successEl.focus();
    }

    bindStars() {
      if (!this.starGroup || !this.stars.length) return;

      this.stars.forEach((btn) => {
        btn.addEventListener('click', () => {
          this.selectRating(parseInt(btn.dataset.star, 10));
        });

        btn.addEventListener('mouseenter', () => {
          this.setStars(parseInt(btn.dataset.star, 10));
        });

        btn.addEventListener('keydown', (e) => this.handleStarKeydown(e));
      });

      this.starGroup.addEventListener('mouseleave', () => {
        this.setStars(this.rating);
      });
    }

    handleStarKeydown(e) {
      let next = null;
      const current = this.rating > 0 ? this.rating : 1;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = Math.min(current + 1, 5);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = Math.max(current - 1, 1);
          break;
        case 'Home':
          next = 1;
          break;
        case 'End':
          next = 5;
          break;
        default:
          return;
      }
      e.preventDefault();
      this.selectRating(next);
      const target = this.stars[next - 1];
      if (target) target.focus();
    }

    selectRating(value) {
      this.rating = value;
      if (this.ratingInput) this.ratingInput.value = this.rating;
      this.setStars(this.rating);
      this.updateStarAria();
      this.updateRovingTabindex();
      this.checkValidity();
    }

    setStars(count) {
      this.stars.forEach((btn, i) => {
        if (i < count) btn.setAttribute('data-star-active', '');
        else           btn.removeAttribute('data-star-active');
      });
    }

    updateStarAria() {
      this.stars.forEach((btn, i) => {
        btn.setAttribute('aria-checked', String(i + 1 === this.rating));
      });
    }

    updateRovingTabindex() {
      const selectedIndex = this.rating > 0 ? this.rating - 1 : 0;
      this.stars.forEach((btn, i) => {
        btn.setAttribute('tabindex', i === selectedIndex ? '0' : '-1');
      });
    }

    checkValidity() {
      if (!this.submitBtn || !this.form) return;
      const hasRating = this.rating > 0;
      const nameField  = this.form.querySelector('[name="review[author]"]');
      const emailField = this.form.querySelector('[name="review[email]"]');
      const titleField = this.form.querySelector('[name="review[title]"]');
      const bodyField  = this.form.querySelector('[name="review[body]"]');
      const hasName  = nameField  && nameField.value.trim().length > 0;
      const hasEmail = emailField && emailField.value.trim().length > 0;
      const hasTitle = titleField && titleField.value.trim().length > 0;
      const hasBody  = bodyField  && bodyField.value.trim().length > 0;
      const isValid  = hasRating && hasName && hasEmail && hasTitle && hasBody;
      if (isValid) this.submitBtn.removeAttribute('aria-disabled');
      else this.submitBtn.setAttribute('aria-disabled', 'true');
      return isValid;
    }

    handleSubmit(e) {
      e.preventDefault();
      if (!this.form || !this.submitBtn) return;
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      this.submitBtn.setAttribute('aria-disabled', 'true');
      if (this.errorEl) this.errorEl.textContent = '';

      const action = this.form.dataset.reviewAction;
      const errGeneric = this.form.dataset.reviewErrorGeneric || '';
      const errTimeout = this.form.dataset.reviewErrorTimeout || '';

      const body = new URLSearchParams();
      body.set('appkey', this.form.dataset.reviewAppkey || '');
      body.set('sku', this.form.dataset.reviewProductId || '');
      body.set('product_title', this.form.dataset.reviewProductTitle || '');
      body.set('product_url', this.form.dataset.reviewProductUrl || '');
      body.set('display_name', this.fieldValue('review[author]'));
      body.set('email', this.fieldValue('review[email]'));
      body.set('review_title', this.fieldValue('review[title]'));
      body.set('review_content', this.fieldValue('review[body]'));
      body.set('review_score', String(this.rating));

      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), 8000);

      fetch(action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
        signal: controller.signal,
      })
        .then((r) => {
          if (r.ok) {
            this.resetForm();
            this.showSuccess();
          } else if (this.errorEl) {
            this.errorEl.textContent = errGeneric;
          }
        })
        .catch((err) => {
          if (!this.errorEl) return;
          this.errorEl.textContent = err.name === 'AbortError' ? errTimeout : errGeneric;
        })
        .finally(() => {
          clearTimeout(t);
          if (this.checkValidity()) this.submitBtn.removeAttribute('aria-disabled');
        });
    }

    fieldValue(name) {
      const field = this.form.querySelector('[name="' + CSS.escape(name) + '"]');
      return field ? field.value.trim() : '';
    }

    resetForm() {
      if (!this.form) return;
      this.form.reset();
      this.rating = 0;
      if (this.ratingInput) this.ratingInput.value = '';
      if (this.errorEl) this.errorEl.textContent = '';
      this.setStars(0);
      this.updateStarAria();
      this.updateRovingTabindex();
      this.checkValidity();
    }
  }

  // Single module-scoped document listeners (NB-runtime-2). Each dispatches to every live
  // instance after a lazy sweep that drops instances whose root has left the DOM, so the
  // document listener count is fixed at two for the page's lifetime regardless of editor reloads.
  function dispatch(method, e) {
    instances.forEach((inst) => {
      if (!inst.root.isConnected) { instances.delete(inst); return; }
      inst[method](e);
    });
  }

  document.addEventListener('click', (e) => dispatch('handleDocumentClick', e));
  document.addEventListener('keydown', (e) => dispatch('handleDocumentKeydown', e));

  function init(scope) {
    scope.querySelectorAll('[data-main-product]').forEach((el) => {
      if (el.hasAttribute('data-review-bound')) return;
      new ReviewDrawer(el);
    });
  }

  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  // Editor section swap/remove: tear the outgoing instance down so its focus trap is released
  // and it stops receiving dispatched events (storefront never fires this).
  document.addEventListener('shopify:section:unload', (e) => {
    instances.forEach((inst) => {
      if (e.target === inst.root || e.target.contains(inst.root)) inst.destroy();
    });
  });
})();
